import { useState } from 'react';
import DefaultImgBussiness from '@assets/images/DefaultImgBussiness.png';
import BtnMenu from '@assets/images/iconos-aside-panel-menu.svg';
import BtnORders from '@assets/images/iconos-aside-panel-orders.svg';
import BtnPubli from '@assets/images/iconos-aside-panel-anuncio.svg';
import onlinePaymentIcon from '@assets/images/onlinePaymentIcon.svg';
import BtnQR from '@assets/images/iconos-aside-panel-qr.svg';
import CouponIcon from '@assets/images/iconos-aside-panel-coupons.svg';
import UsersIcon from '@assets/images/icono-mis clientes.svg';
import IconoQ from '@assets/images/icono-question.svg';
import priceIcon from '@assets/images/priceIcon.svg';
import IconoEstadisticas from '@assets/images/iconos-aside-panel-estadisticas.svg';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import useModules from '@utils/hooks/useModules';
import NavBarBackground from './components/layout/NavBarBackground';
import NavBarContainerV2 from './components/layout/NavBarContainerV2';
import NavBarToggleButton from './components/interface/NavBarToggleButton';
import NavBarButton from './components/interface/NavBarButton';
import NavBarStatus from './components/interface/NavBarStatus';
import { ModuleName } from '@utils/constants';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import Loading from '@components/LoadingProcess';
import { IconBuildingBridge2, IconSettingsAutomation, IconLinkPlus } from '@tabler/icons-react';
import { useShouldShowSetup } from '@pages/Setup/hooks/useShouldShowSetup';
import { Icon } from '@iconify/react';
import FounderContactModal from '@components/interface/FounderContactModal';
import FounderMessageForm from '@components/interface/FounderMessageForm';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

const getNavigatePath = (hasModule: boolean, path: string) => {
  return hasModule ? path : 'suscripcion/' + path;
};

// eslint-disable-next-line complexity
const NavBarDesktopV2 = () => {
  const navigate = useNavigate();
  const [isExpanded, setExpanded] = useState(true);
  const [isConfigOpen, setIsConfigOpen] = useState(true);
  const { user } = useAuth();
  const { isFreemium, planId } = useSubscription();
  const { commerce, isFetching } = useGetCommerce();
  const { hasModule } = useModules();
  const shouldShowSetup = useShouldShowSetup();
  const [showFounderWarning, setShowFounderWarning] = useState(false);
  const [showFounderForm, setShowFounderForm] = useState(false);
  const { trackEvent } = useTracking();

  return (
    <NavBarBackground isExpanded={isExpanded}>
      <NavBarToggleButton isExpanded={isExpanded} onToggle={() => setExpanded(!isExpanded)} />
      {!planId ? (
        <Loading />
      ) : (
        <NavBarContainerV2>
          {/* Sección superior: Logo e información */}
          <div className="pb-6 border-b border-gray-200">
            {isExpanded ? (
              /* Layout horizontal cuando está expandido */
              <div className="flex items-center gap-4">
                {/* Logo con indicador de estado */}
                <div className="relative flex-shrink-0">
                  <div className="rounded-full border-4 border-gray-700 overflow-hidden w-20 h-20">
                    <img
                      src={commerce.logotipo || DefaultImgBussiness}
                      alt="Logo del comercio"
                      className="w-full h-full object-cover bg-white"
                    />
                  </div>
                  {/* Indicador de estado verde */}
                  <div className="absolute top-0 right-0 bg-white rounded-full p-0.5 shadow-md">
                    <NavBarStatus isOpen={commerce.servicio_activo ?? true} showLabel={false} size="md" />
                  </div>
                </div>

                {/* Información del comercio a la derecha */}
                <div className="flex-1 min-w-0">
                  {/* Nombre del comercio */}
                  <h2 className="text-base font-bold text-gray-900 mb-1 truncate">{commerce.nombre}</h2>

                  {/* Bienvenida al usuario */}
                  <p className="text-sm text-gray-600">
                    Bienvenido: <span className="font-bold text-gray-900">{user.username}</span>
                  </p>
                </div>
              </div>
            ) : (
              /* Logo centrado cuando está colapsado */
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-14 h-14 rounded-full border-4 border-gray-700 overflow-hidden">
                    <img
                      src={commerce.logotipo || DefaultImgBussiness}
                      alt="Logo del comercio"
                      className="w-full h-full object-cover bg-white"
                    />
                  </div>
                  {/* Badge de estado también en versión colapsada */}
                  <div className="absolute top-0 right-0 bg-white rounded-full p-0.5 shadow-sm">
                    <NavBarStatus isOpen={commerce.servicio_activo ?? true} showLabel={false} size="sm" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sección media: Todos los botones con scroll */}
          <div className="flex-1 flex lg:flex-col mt-2 overflow-y-auto overflow-x-hidden">
            {/* Configuración inicial */}
            {shouldShowSetup && (
              <NavBarButton
                isExpanded={isExpanded}
                to="configuracion-inicial"
                id="configuracion-inicial-link"
                icon=""
                tablerIcon={<IconSettingsAutomation color="#4298b5" stroke={2} />}
                label="Configuración inicial"
              />
            )}

            {/* Control de pedidos */}
            {(isFreemium || hasModule(ModuleName.HISTORIAL)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="control-pedidos"
                id="control-pedidos-link"
                icon={BtnORders}
                label="Control de pedidos"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.HISTORIAL)}
              />
            )}

            {/* Estadísticas */}
            {(isFreemium || hasModule(ModuleName.ESTADISTICAS)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="estadisticas"
                id="estadisticas-link"
                icon={IconoEstadisticas}
                label="Estadísticas"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ESTADISTICAS)}
              />
            )}

            {/* Menú */}
            {(isFreemium || hasModule(ModuleName.MENU)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="menu"
                id="menu-link"
                icon={BtnMenu}
                label="Menú"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.MENU)}
              />
            )}

            {/* Cupones */}
            {(isFreemium || hasModule(ModuleName.CUPONES)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="cupones"
                id="cupones-link"
                icon={CouponIcon}
                label="Cupones"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.CUPONES)}
              />
            )}

            {/* Mis Mesas */}
            {(isFreemium || hasModule(ModuleName.SELF_SERVICE)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="mesas"
                id="mesas-link"
                tablerIcon={<IconBuildingBridge2 color="#4298b5" stroke={2} />}
                icon=""
                label="Mis Mesas"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.SELF_SERVICE)}
              />
            )}

            {/* Cobros en línea */}
            {(isFreemium || hasModule(ModuleName.COBROS_EN_LINEA)) && !isFetching && commerce?.business_id && (
              <NavBarButton
                isExpanded={isExpanded}
                to="pedidos-en-linea"
                id="pedidos-en-linea-link"
                icon={onlinePaymentIcon}
                label="Cobros en línea"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.COBROS_EN_LINEA)}
              />
            )}

            {/* Crear anuncios */}
            {(isFreemium || hasModule(ModuleName.ANUNCIOS)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="anuncios"
                id="anuncios-link"
                icon={BtnPubli}
                label="Crear anuncios"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ANUNCIOS)}
              />
            )}

            {/* Compartir menú */}
            {(isFreemium || hasModule(ModuleName.COMPARTIR)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="compartir-menu"
                id="compartir-link"
                icon={BtnQR}
                label="Compartir menú"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.COMPARTIR)}
              />
            )}

            {/* Perfil Social */}
            {(isFreemium || hasModule(ModuleName.LINK_PROFILE)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="perfil-social"
                id="perfil-social-link"
                tablerIcon={<IconLinkPlus color="#4298b5" stroke={2} />}
                icon=""
                label="Perfil Social"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.LINK_PROFILE)}
              />
            )}

            {/* Asistencia Técnica */}
            {(isFreemium || hasModule(ModuleName.ASISTENCIA_TECNICA)) && (
              <NavBarButton
                isExpanded={isExpanded}
                to="asistencia"
                id="asistencia-link"
                icon={IconoQ}
                label="Asistencia Técnica"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ASISTENCIA_TECNICA)}
              />
            )}

            {/* DROPDOWN DE AJUSTES */}
            <div className="flex flex-col">
              {/* Botón principal de Ajustes */}
              <button
                onClick={() => setIsConfigOpen(!isConfigOpen)}
                className={`flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light transition-colors text-color-gray_ultra_dark ${
                  isConfigOpen ? 'bg-color-gray_light' : ''
                }`}
              >
                <Icon icon="fa6-solid:gear" color="#4298b5" width="20" className="w-5 flex-shrink-0" />
                <div
                  className={`overflow-hidden text-left transition-all ${isExpanded ? 'ml-2 w-full opacity-100' : 'w-0 opacity-0'}`}
                >
                  <span className="whitespace-nowrap ">Ajustes</span>
                </div>
                {isExpanded ? (
                  <Icon
                    icon="bxs:chevron-down"
                    className={`transition-transform flex-shrink-0 ${isConfigOpen ? 'rotate-180' : ''}`}
                    width="16"
                  />
                ) : null}
              </button>

              {/* Sub-opciones del dropdown */}
              {isConfigOpen && isExpanded && (
                <div className="flex flex-col pl-4 mt-1">
                  {/* Ayúdanos a Mejorar */}
                  <button
                    onClick={() => {
                      trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
                      setShowFounderWarning(true);
                    }}
                    className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                  >
                    <Icon icon="tabler:message-2-filled" color="#4298b5" width="16" className="mr-2" />
                    <span>Ayúdanos a mejorar</span>
                  </button>
                  {/* Datos del comercio */}
                  {(isFreemium || hasModule(ModuleName.DATOS_COMERCIO)) && (
                    <button
                      onClick={() =>
                        navigate(getNavigatePath(hasModule(ModuleName.DATOS_COMERCIO) || false, 'datos-del-comercio'))
                      }
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <Icon icon="fa-solid:store" color="#4298b5" width="16" className="mr-2" />
                      <span>Datos del comercio</span>
                      {!hasModule(ModuleName.DATOS_COMERCIO) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {/* Horarios */}
                  {(isFreemium || hasModule(ModuleName.HORARIOS)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.HORARIOS) || false, 'horarios'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <Icon icon="bxs:time-five" color="#4298b5" width="16" className="mr-2" />
                      <span>Horarios</span>
                      {!hasModule(ModuleName.HORARIOS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {/* Tarifas y entregas */}
                  {(isFreemium || hasModule(ModuleName.TARIFAS)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.TARIFAS) || false, 'tarifas'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <img src={priceIcon} alt="Tarifas" className="w-4 h-4 mr-2" />
                      <span>Tarifas y entregas</span>
                      {!hasModule(ModuleName.TARIFAS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {/* Alta de usuarios */}
                  {(isFreemium || hasModule(ModuleName.USUARIOS)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.USUARIOS) || false, 'usuarios'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <img src={UsersIcon} alt="Usuarios" className="w-4 h-4 mr-2" />
                      <span>Alta de usuarios</span>
                      {!hasModule(ModuleName.USUARIOS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {/* Ajustes */}
                  {(isFreemium || hasModule(ModuleName.AJUSTES)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.AJUSTES) || false, 'ajustes'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <Icon icon="fa6-solid:gear" color="#4298b5" width="16" className="mr-2" />
                      <span>Ajustes</span>
                      {!hasModule(ModuleName.AJUSTES) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {/* Integraciones */}
                  <button
                    onClick={() => navigate('/integraciones')}
                    className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                  >
                    <Icon icon="mdi:plug" color="#4298b5" width="16" className="mr-2" />
                    <span>Integraciones</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </NavBarContainerV2>
      )}

      {/* Modals de Feedback */}
      {showFounderWarning ? (
        <FounderContactModal
          onClose={() => setShowFounderWarning(false)}
          onContinue={() => {
            setShowFounderWarning(false);
            setShowFounderForm(true);
          }}
        />
      ) : null}
      {showFounderForm ? <FounderMessageForm commerce={commerce} onClose={() => setShowFounderForm(false)} /> : null}
    </NavBarBackground>
  );
};

export default NavBarDesktopV2;
